'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createClient, suppressSupabaseAuthErrors } from '@/lib/supabase/client';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  avatar_url: string;
  role: 'farmer' | 'admin';
  phone: string;
  region: string;
  is_active: boolean;
  created_at: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signInWithGoogle: (role?: string) => Promise<any>;
  signInWithFacebook: () => Promise<any>;
  signInWithPhone: (phone: string) => Promise<any>;
  verifyOtp: (phone: string, token: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Suppress Supabase internal auth error logs as early as possible —
// before any client instance is created or any token refresh fires.
suppressSupabaseAuthErrors();

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Create client once and reuse — prevents multiple subscriptions on re-render
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  // Guard against rapid repeated auth state changes (prevents rate limit cascade)
  const lastAuthEventRef = useRef<{ event: string; time: number }>({ event: '', time: 0 });

  const clearStaleTokens = () => {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith('sb-') ||
          key.startsWith('sb_') ||
          key.includes('supabase') ||
          key.includes('auth-token')
        ) {
          localStorage.removeItem(key);
        }
      });
    } catch {}
    try {
      document.cookie.split(';').forEach((c) => {
        const name = c.trim().split('=')[0];
        if (
          name.startsWith('sb-') ||
          name.startsWith('sb_') ||
          name.includes('supabase') ||
          name.includes('auth-token')
        ) {
          document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
        }
      });
    } catch {}
  };

  const isRefreshTokenError = (error: any): boolean => {
    if (!error) return false;
    return (
      error.message?.includes('Refresh Token Not Found') ||
      error.message?.includes('Invalid Refresh Token') ||
      error.code === 'refresh_token_not_found' ||
      (error as any).__isAuthError === true
    );
  };

  const isRateLimitError = (error: any): boolean => {
    if (!error) return false;
    return (
      error.message?.includes('rate limit') ||
      error.message?.includes('Rate limit') ||
      error.status === 429 ||
      error.code === 'over_request_rate_limit'
    );
  };

  const handleStaleSession = () => {
    clearStaleTokens();
    // Use local scope to avoid making a server request with an invalid token
    supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    setSession(null);
    setUser(null);
    setProfile(null);
    setLoading(false);
  };

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (!error && data) {
        setProfile(data as UserProfile);
      }
    } catch {
      // Profile fetch failed silently
    }
  };

  useEffect(() => {
    // NOTE: console.error/warn suppression is now applied at module load time
    // via suppressSupabaseAuthErrors() above — no need to re-apply here.

    // Clear any stale tokens immediately before attempting getSession
    // This prevents the auto-refresh loop from firing repeatedly
    const initAuth = async () => {
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          if (isRefreshTokenError(error) || isRateLimitError(error)) {
            handleStaleSession();
            return;
          }
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }

        if (initialSession) {
          const expiresAt = initialSession.expires_at ?? 0;
          const nowSec = Math.floor(Date.now() / 1000);
          // If token is already expired, clear the stale session immediately
          // instead of attempting a refresh (which causes Invalid Refresh Token errors)
          if (expiresAt > 0 && expiresAt < nowSec) {
            handleStaleSession();
            return;
          }
          setSession(initialSession);
          setUser(initialSession.user);
          fetchProfile(initialSession.user.id);
        } else {
          setSession(null);
          setUser(null);
        }
        setLoading(false);
      } catch (err: any) {
        if (isRefreshTokenError(err) || isRateLimitError(err)) {
          handleStaleSession();
        } else {
          setSession(null);
          setUser(null);
          setProfile(null);
          setLoading(false);
        }
      }
    };

    initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // Debounce: ignore duplicate events within 500ms to prevent rate limit cascade
      const now = Date.now();
      if (
        lastAuthEventRef.current.event === event &&
        now - lastAuthEventRef.current.time < 500
      ) {
        return;
      }
      lastAuthEventRef.current = { event, time: now };

      // When token refresh fails, Supabase fires TOKEN_REFRESHED with null session
      // or SIGNED_OUT — both mean the session is gone
      if (
        event === 'SIGNED_OUT' ||
        (event === 'TOKEN_REFRESHED' && !newSession)
      ) {
        clearStaleTokens();
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        if (newSession) {
          setSession(newSession);
          setUser(newSession.user);
          fetchProfile(newSession.user.id);
        }
        setLoading(false);
        return;
      }

      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        fetchProfile(newSession.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    // Global handler for unhandled Supabase auth errors (background token refresh)
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      if (isRefreshTokenError(reason) || isRateLimitError(reason)) {
        event.preventDefault();
        if (isRefreshTokenError(reason)) {
          handleStaleSession();
        }
        // For rate limit errors, just prevent the unhandled rejection — don't clear session
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: metadata?.role || 'farmer',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signInWithGoogle = async (role?: string) => {
    // Store role in a cookie so the server-side callback can read it
    if (role && typeof document !== 'undefined') {
      document.cookie = `pending_signup_role=${role}; path=/; max-age=300; SameSite=Lax`;
    }
    // Use the current window origin so the redirect URL matches whichever domain
    // the user is on (agroguard2361.builtwithrocket.new OR agroai.online).
    // Both domains must be whitelisted in Supabase Auth > URL Configuration > Redirect URLs
    // and in Google Cloud Console > OAuth 2.0 > Authorized redirect URIs.
    const siteUrl =
      typeof window !== 'undefined'
        ? window.location.origin
        : (process.env.NEXT_PUBLIC_SITE_URL || 'https://agroguard2361.builtwithrocket.new');

    // Preserve the 'next' param so the callback can redirect to the intended page
    const nextParam =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('next')
        : null;

    const redirectTo = nextParam
      ? `${siteUrl}/auth/callback?next=${encodeURIComponent(nextParam)}`
      : `${siteUrl}/auth/callback`;

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: { access_type: 'offline', prompt: 'consent' },
      },
    });
    if (error) throw error;
    return data;
  };

  const signInWithFacebook = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signInWithPhone = async (phone: string) => {
    const { data, error } = await supabase.auth.signInWithOtp({
      phone,
    });
    if (error) throw error;
    return data;
  };

  const verifyOtp = async (phone: string, token: string) => {
    const { data, error } = await supabase.auth.verifyOtp({
      phone,
      token,
      type: 'sms',
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    clearStaleTokens();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data as UserProfile;
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithFacebook,
    signInWithPhone,
    verifyOtp,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
